<script setup>
import { useI18n } from 'vue-i18n';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SupportTicketAttachmentsField from './SupportTicketAttachmentsField.vue';
import { useSupportTicketAttachments } from '../composables/useSupportTicketAttachments';

defineProps({
  statusOptions: { type: Array, default: () => [] },
  canViewAccountTickets: { type: Boolean, default: false },
  isReplying: { type: Boolean, default: false },
  canReply: { type: Boolean, default: false },
});

const emit = defineEmits(['submit']);

const replyForm = defineModel({ type: Object, required: true });

const { t } = useI18n();
const { attachmentLimitLabel, appendFiles } = useSupportTicketAttachments();

const onReplyFilesSelected = event => {
  appendFiles(replyForm.value, event.target.files);
  event.target.value = '';
};

const removeReplyAttachment = index => {
  replyForm.value.attachments.splice(index, 1);
};
</script>

<template>
  <form
    class="grid gap-3 border-t border-lais-border-soft pt-4"
    @submit.prevent="emit('submit')"
  >
    <LaisField :label="t('SUPPORT_TICKETS.FORM.REPLY')">
      <textarea
        v-model.trim="replyForm.body"
        rows="4"
        class="min-h-[112px] resize-y rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] text-lais-text outline-none focus:border-lais-primary"
        :placeholder="t('SUPPORT_TICKETS.FORM.REPLY_PLACEHOLDER')"
      />
    </LaisField>

    <SupportTicketAttachmentsField
      :attachments="replyForm.attachments"
      :limit-label="attachmentLimitLabel"
      @change="onReplyFilesSelected"
      @remove="index => removeReplyAttachment(index)"
    />

    <div
      class="grid gap-3 sm:grid-cols-[minmax(180px,260px)_auto] sm:items-end"
    >
      <LaisField :label="t('SUPPORT_TICKETS.FORM.CHANGE_STATUS')">
        <select
          v-model="replyForm.product_status"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
        >
          <option value="">
            {{ t('SUPPORT_TICKETS.FORM.KEEP_STATUS') }}
          </option>
          <option
            v-for="item in statusOptions.slice(1)"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </option>
        </select>
      </LaisField>

      <div class="grid gap-2">
        <label
          v-if="canViewAccountTickets"
          class="inline-flex items-center gap-2 text-[12px] font-semibold text-lais-text"
        >
          <input v-model="replyForm.internal" type="checkbox" />
          <span>{{ t('SUPPORT_TICKETS.FORM.INTERNAL') }}</span>
        </label>
        <p
          v-if="canViewAccountTickets && replyForm.internal"
          class="text-[11px] text-lais-text-muted"
        >
          {{ t('SUPPORT_TICKETS.TRIAGE.INTERNAL_HELPER') }}
        </p>
        <div class="flex justify-end">
          <LaisPrimaryButton
            type="submit"
            icon="i-lucide-send"
            :is-loading="isReplying"
            :disabled="!canReply"
          >
            {{
              isReplying && replyForm.attachments.length
                ? t('SUPPORT_TICKETS.ATTACHMENTS.UPLOADING')
                : t('SUPPORT_TICKETS.FORM.REPLY_SUBMIT')
            }}
          </LaisPrimaryButton>
        </div>
      </div>
    </div>
  </form>
</template>
